import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileDown, CheckCircle2, Sheet, Search, Trash2 } from 'lucide-react';
import { qd, money, STATUS_COLORS, apiError } from '../api';
import { useQdAuth } from '../QdApp';

const downloadPdf = async (q) => {
  try {
    const { data } = await qd.get(`/quotes/${q.id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${q.number}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
  } catch (e) {
    alert(`PDF download failed: ${e.response?.status || e.message}`);
  }
};

const exportExcel = async (filter) => {
  try {
    const { data } = await qd.get(`/quotes/export?status=${filter}`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `quotes-${filter}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
  } catch (e) {
    alert(`Export failed: ${e.response?.status || e.message}`);
  }
};

const STATUSES = ['all', 'draft', 'sent', 'accepted', 'expired'];

export default function QdQuotes() {
  const { user } = useQdAuth();
  const isSuper = user.role === 'superadmin';
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { qd.get('/quotes').then((r) => setQuotes(r.data)); }, []);

  const markAccepted = async (q) => {
    if (!window.confirm(`Mark ${q.number} as accepted?`)) return;
    try {
      const { data } = await qd.patch(`/quotes/${q.id}`, { status: 'accepted' });
      setQuotes((prev) => prev.map((x) => (x.id === q.id ? data : x)));
    } catch (e) {
      alert(apiError(e));
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Delete quote ${q.number} permanently? This cannot be undone.`)) return;
    try {
      await qd.delete(`/quotes/${q.id}`);
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
    } catch (e) {
      alert(apiError(e));
    }
  };

  const shown = quotes.filter((q) => {
    if (filter !== 'all' && q.status !== filter) return false;
    const s = search.trim().toLowerCase();
    return !s || q.number.toLowerCase().includes(s) || (q.client_snapshot?.name || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-xl font-bold text-[#14233F]">Quotes</h1>
        <div className="flex gap-1.5 items-center flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client or number…"
              className="border border-slate-300 rounded pl-8 pr-3 py-1.5 text-sm w-56" data-testid="qd-quotes-search" />
          </div>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded border ${filter === s ? 'bg-[#14233F] text-white border-[#14233F]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              data-testid={`qd-filter-${s}`}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              <span className="ml-1.5 mono text-[10px] opacity-60">
                {s === 'all' ? quotes.length : quotes.filter((q) => q.status === s).length}
              </span>
            </button>
          ))}
          <button onClick={() => exportExcel(filter)} title="Export filtered list to Excel"
            className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-[#0F7D70] text-[#0F7D70] hover:bg-teal-50"
            data-testid="qd-export-excel">
            <Sheet className="h-3.5 w-3.5" /> Export Excel
          </button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3 font-semibold">Number</th>
              <th className="text-left px-4 py-3 font-semibold">Client</th>
              <th className="text-left px-4 py-3 font-semibold">Entity</th>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-right px-4 py-3 font-semibold">Total (excl. VAT)</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">By</th>
              <th className="text-right px-4 py-3 font-semibold">PDF</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((q) => (
              <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <Link to={`/quote/devis/${q.id}`} className="mono font-semibold text-[#0F7D70] hover:underline" data-testid={`qd-quote-link-${q.number}`}>
                    {q.number}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-800">{q.client_snapshot?.name}</td>
                <td className="px-4 py-3 mono text-xs text-slate-500">{q.entity_id.toUpperCase()}</td>
                <td className="px-4 py-3 mono text-xs text-slate-500">{q.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 mono text-right">{money(q.totals?.net)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${STATUS_COLORS[q.status]}`}>{q.status}</span>
                    {q.status === 'sent' && (
                      <button onClick={() => markAccepted(q)} title="Mark as accepted"
                        className="text-slate-300 hover:text-emerald-600" data-testid={`qd-accept-${q.number}`}>
                        <CheckCircle2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{q.created_by_email}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2.5">
                    <button onClick={() => downloadPdf(q)} title={q.pdf_url ? 'Download sent PDF (archived)' : 'Download PDF'}
                      data-testid={`qd-pdf-${q.number}`}>
                      <FileDown className="h-4 w-4 text-[#0F7D70] hover:text-[#14233F]" />
                    </button>
                    {isSuper && (
                      <button onClick={() => deleteQuote(q)} title="Delete quote" data-testid={`qd-delete-quote-${q.number}`}>
                        <Trash2 className="h-4 w-4 text-slate-300 hover:text-red-600" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400 text-sm">No quotes yet — create your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
